import { LayersTree } from '@/components/builder/layers-tree';
import { cn } from '@/lib/cn';
import type { BuilderPanelId } from '@/lib/builder-preferences';
import { Images, Layers3, Palette } from 'lucide-react';

const PANELS = [
  { id: 'layers', label: 'Layers', icon: Layers3 },
  { id: 'theme', label: 'Theme', icon: Palette },
  { id: 'assets', label: 'Assets', icon: Images },
] as const satisfies readonly { id: BuilderPanelId; label: string; icon: typeof Layers3 }[];

/** One mutually-exclusive left panel. Component insertion still floats over the canvas, so none of
 *  these workspaces competes with creation controls for vertical space. The 40px rail lives INSIDE
 *  the old responsive width budget: adding Theme/Assets does not make the canvas narrower. */
export function BuilderSidebar({
  activePanel,
  onPanelChange,
  layers,
  theme,
  assets,
}: {
  activePanel: BuilderPanelId;
  onPanelChange: (panel: BuilderPanelId) => void;
  layers: React.ComponentProps<typeof LayersTree>;
  theme: React.ReactNode;
  assets: React.ReactNode;
}): React.JSX.Element {
  const panel = PANELS.find((item) => item.id === activePanel)!;

  return (
    <aside
      aria-label="Builder panels"
      className="flex w-[220px] shrink-0 border-r bg-background xl:w-[248px] 2xl:w-[304px]"
    >
      <nav aria-label="Editor panels" className="flex w-10 shrink-0 flex-col items-center gap-1 border-r py-1.5">
        {PANELS.map((item) => {
          const Icon = item.icon;
          const active = item.id === activePanel;
          return (
            <button
              key={item.id}
              type="button"
              aria-label={item.label}
              aria-pressed={active}
              title={item.label}
              className={cn(
                'flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
                active && 'bg-secondary text-foreground'
              )}
              onClick={() => onPanelChange(item.id)}
            >
              <Icon className="size-4" />
            </button>
          );
        })}
      </nav>

      <section aria-label={panel.label} className="flex min-w-0 min-h-0 flex-1 flex-col">
        <h2 className="flex h-11 shrink-0 items-center border-b px-3 text-[12px] font-semibold">
          {panel.label}
        </h2>
        <div className={cn('min-h-0 flex-1 overflow-y-auto', activePanel === 'layers' && 'px-1.5 py-1')}>
          {activePanel === 'layers' ? <LayersTree {...layers} /> : activePanel === 'theme' ? theme : assets}
        </div>
      </section>
    </aside>
  );
}
